import {
  isEndpointAllowed,
  isOriginTrusted,
} from '@de/config/allowed-data-sources';
import { interpolateObject } from '@de/utils/interpolation';
import { contextWithoutEndpointExcludedFormState } from './endpointExcludedFormKeys';
import { omitEmptyFormStateQueryKeys } from './omitEmptyFormStateQuery';
import {
  DataSourceHttpError,
  DataSourceEndpointNotAllowedError,
  type DataSourceDeclaration,
  type HttpDataSourceDeclaration,
  type DataSourceStatus,
} from '@de/providers/DataSourceManager.types';
import { extractResponsePath } from './extractResponsePath';

/**
 * Default per-attempt timeout (ms) when a source omits `errorStrategy.timeout`.
 * Matches child SAD §10 recommendation.
 */
export const DEFAULT_TIMEOUT_MS = 10_000;

/**
 * Default retry count (additional attempts after the first) when a source
 * omits `errorStrategy.retries`.
 */
export const DEFAULT_RETRIES = 2;

/**
 * Base delay for exponential backoff between retries. The nth retry waits
 * `BASE_BACKOFF_MS * 2^(n-1)` milliseconds (so 200, 400, 800, …). Kept
 * small because DSM fetches are user-visible; aggressive retries harm UX.
 */
export const BASE_BACKOFF_MS = 200;

export interface InterpolatedRequest {
  readonly endpoint: string;
  readonly method: 'GET' | 'POST';
  readonly headers?: Record<string, string>;
  readonly body?: unknown;
  readonly query?: Record<string, string | readonly string[]>;
}

export interface FetchDataSourceResult {
  readonly status: DataSourceStatus;
  readonly data?: unknown;
  readonly error?: Error;
  readonly request: InterpolatedRequest;
}

export interface FetchDataSourceOptions {
  readonly signal?: AbortSignal;
  readonly timeout?: number;
  readonly retries?: number;
  /** Injectable for tests. Defaults to `globalThis.fetch`. */
  readonly fetchImpl?: typeof fetch;
  /** Injectable for tests. Defaults to `setTimeout`/`clearTimeout`. */
  readonly setTimeoutImpl?: typeof setTimeout;
  readonly clearTimeoutImpl?: typeof clearTimeout;
  /** Delay helper between retries. Defaults to `setTimeout`-based Promise. */
  readonly delay?: (ms: number, signal?: AbortSignal) => Promise<void>;
  /**
   * Origins that absolute-URL endpoints are allowed to fetch from. When
   * omitted/empty, absolute URLs are rejected (fail-closed) and only
   * path-only endpoints (`/v1/...`) are accepted. See
   * {@link isOriginTrusted} for the full contract.
   */
  readonly trustedOrigins?: readonly string[];
  /**
   * Canonical runtime tenant from trusted host context (never authored
   * DSL). Required for allowlist-scoped sources such as Product API;
   * omitted/blank denies those sources. Global sources ignore it.
   */
  readonly tenantId?: string;
  /**
   * Optional last-mile transform applied to the **interpolated**
   * request immediately before allowlist + origin checks. The transform
   * receives the resolved endpoint, headers, body, and query, and
   * returns a (possibly new) request. It MUST be pure -- DSM uses the
   * returned request both for the network call and for downstream
   * status reporting.
   *
   * This hook exists so service-specific runtime rules (today: Mobius
   * numeric-ID coercion in `body.criteria.*`) can run after `formState`
   * interpolation -- which is the only point where the actual values
   * (vs. `{{…}}` templates) exist -- without `fetchDataSource` knowing
   * about any particular upstream service. The bridge layer
   * (ConfigurableStep) builds the transform from `apiConfig` and
   * passes it in; standalone hosts can omit it.
   */
  readonly coerceRequestBody?: (
    req: InterpolatedRequest,
  ) => InterpolatedRequest;
  /**
   * Request dispatch already built and coerced. When set, this function sends
   * it and does not interpolate or coerce again — a second shaping would be a
   * different request from the one the cache key describes.
   */
  readonly preparedRequest?: InterpolatedRequest;
  /**
   * Optional service-shaped transform applied to the raw parsed response
   * JSON immediately after `response.json()` and **before** `responsePath`
   * extraction. Symmetric to {@link coerceRequestBody}: it lets the bridge
   * reshape an upstream payload into the contract the DSL/UI expects
   * (today: normalizing Offerings `/match` bucket items into the flat
   * `Result` card contract) without `fetchDataSource` knowing about any
   * particular service. It receives the parsed JSON and the resolved
   * request so it can gate on the endpoint, and MUST be pure. Runs on
   * every successful (2xx) response; omit for hosts that need no
   * reshaping.
   */
  readonly coerceResponse?: (
    res: unknown,
    req: InterpolatedRequest,
    decl?: DataSourceDeclaration,
  ) => unknown;
}

/**
 * Resolve `decl` into a concrete request and fetch it.
 *
 * Pipeline (child SAD ADR-10):
 *   1. Interpolate `endpoint`, `headers`, `body`, `query` via DB-1.2
 *      `interpolateObject` against `interpolationContext`.
 *   2. Run {@link isEndpointAllowed} against the resolved endpoint — if
 *      false, return `status: 'error'` with {@link DataSourceEndpointNotAllowedError}.
 *      No network call is made.
 *   3. Fetch with a per-attempt `AbortController` + timeout. On 5xx / network
 *      error, retry with exponential backoff up to `retries` additional
 *      attempts. 4xx returns immediately (client error — retries will not
 *      help). The caller's `signal` is honored and aborts the whole flow.
 *   4. Parse JSON, apply optional `coerceResponse` (service-shaped reshape),
 *      then run `extractResponsePath(coerced, decl.responsePath)`.
 *   5. Classify empty (`[]`, `{}`, `null`, `undefined`) vs `resolved`.
 *
 * Never throws — always resolves with a {@link FetchDataSourceResult}.
 * This keeps the provider reducer simple: a thrown exception on one source
 * must not take down the tree (SAD §10 "no single data source failure
 * crashes the entire experience").
 */
export async function fetchDataSource(
  decl: HttpDataSourceDeclaration,
  interpolationContext: unknown,
  options: FetchDataSourceOptions = {},
): Promise<FetchDataSourceResult> {
  const interpolated = options.preparedRequest
    ? options.preparedRequest
    : interpolateHttpRequest(decl, interpolationContext);
  const request =
    options.preparedRequest || !options.coerceRequestBody
      ? interpolated
      : options.coerceRequestBody(interpolated);

  if (!isEndpointAllowed(request.endpoint, request.method, options.tenantId)) {
    // Surface the policy violation to ops/security observers (review
    // warning #1 — EDUN-10683 AC: "Allowlist blocks unlisted endpoints
    // at runtime with logging"). Browser-safe `console.warn` matches
    // the package's existing logging convention; production telemetry
    // can hook on this via `window.console` interceptors. Keep this
    // separate from the typed error returned to the consumer so
    // monitoring doesn't depend on whether a particular consumer
    // reads `source.error`.
    // eslint-disable-next-line no-console
    console.warn(
      `[DataSourceManager] blocked endpoint by allowlist: ${request.method} ${request.endpoint}`,
    );
    return {
      status: 'error',
      request,
      error: new DataSourceEndpointNotAllowedError(
        request.endpoint,
        request.method,
      ),
    };
  }

  // Origin trust check (review #1 re-review, R. Vazquez). The path
  // allowlist above does not constrain the actual network
  // destination when the DSL passes an absolute URL: a declaration
  // like `https://evil.example.com/v1/search` would otherwise pass
  // (the path `/v1/search` is allowed) and `fetch()` would exfiltrate
  // interpolated headers / body to the attacker-controlled origin.
  // Validate the **fetch target** before issuing `fetch()`. Path-only
  // endpoints bypass this check (they carry no origin in the
  // declaration; the host's base-URL resolver — DB-1.5 — owns origin
  // selection for those).
  if (!isOriginTrusted(request.endpoint, options.trustedOrigins)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[DataSourceManager] blocked endpoint by origin allowlist: ${request.method} ${request.endpoint} (trustedOrigins: ${
        options.trustedOrigins?.join(', ') ?? '<none configured>'
      })`,
    );
    return {
      status: 'error',
      request,
      error: new DataSourceEndpointNotAllowedError(
        request.endpoint,
        request.method,
      ),
    };
  }

  const timeout = decl.errorStrategy?.timeout ?? DEFAULT_TIMEOUT_MS;
  const retries = decl.errorStrategy?.retries ?? DEFAULT_RETRIES;
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  const delay = options.delay ?? defaultDelay;

  let lastError: Error = new Error(
    'DataSource fetch never attempted — unreachable.',
  );

  for (let attempt = 0; attempt <= retries; attempt++) {
    if (options.signal?.aborted) {
      return {
        status: 'error',
        request,
        error: toError(options.signal.reason, 'DataSource fetch aborted.'),
      };
    }

    try {
      const response = await fetchOnce(
        request,
        fetchImpl,
        timeout,
        options.signal,
        options.setTimeoutImpl,
        options.clearTimeoutImpl,
      );

      if (response.status >= 400 && response.status < 500) {
        return {
          status: 'error',
          request,
          error: new DataSourceHttpError(
            response.status,
            request.endpoint,
            request.method,
          ),
        };
      }
      if (response.status >= 500) {
        lastError = new DataSourceHttpError(
          response.status,
          request.endpoint,
          request.method,
        );
        if (attempt < retries) {
          await delay(backoffMs(attempt), options.signal);
          continue;
        }
        return { status: 'error', request, error: lastError };
      }

      const json = (await response.json()) as unknown;
      const coerced = options.coerceResponse
        ? options.coerceResponse(json, request, decl)
        : json;
      const extracted = extractResponsePath(coerced, decl.responsePath);

      if (isEmptyPayload(extracted)) {
        return { status: 'empty', request, data: extracted };
      }
      return { status: 'resolved', request, data: extracted };
    } catch (error) {
      lastError = toError(error, 'DataSource fetch failed.');
      if (isAbortError(error) || options.signal?.aborted) {
        return { status: 'error', request, error: lastError };
      }
      if (attempt < retries) {
        try {
          await delay(backoffMs(attempt), options.signal);
        } catch {
          return { status: 'error', request, error: lastError };
        }
        continue;
      }
      return { status: 'error', request, error: lastError };
    }
  }

  return { status: 'error', request, error: lastError };
}

/**
 * Shape a declaration into the request this module will send.
 *
 * Exported because the cache key must be derived from the same shaping, not
 * from a parallel copy of it. A second implementation that forgets a field is
 * two different requests sharing one cache entry, which surfaces as one
 * source rendering another's data rather than as an error.
 */
export function interpolateHttpRequest(
  decl: HttpDataSourceDeclaration,
  context: unknown,
): InterpolatedRequest {
  const endpoint = String(
    interpolateObject(
      decl.endpoint,
      contextWithoutEndpointExcludedFormState(context),
    ),
  );
  // Host-seeded values may ride in the query and body. Headers share the
  // endpoint's filtered context so a raw host value is not forwarded.
  const headers = decl.headers
    ? interpolateObject(
        decl.headers,
        contextWithoutEndpointExcludedFormState(context),
      )
    : undefined;
  const body =
    decl.body !== undefined ? interpolateObject(decl.body, context) : undefined;
  const query = decl.query
    ? interpolateObject(
        omitEmptyFormStateQueryKeys(decl.query, context),
        context,
      )
    : undefined;

  return {
    endpoint,
    method: decl.method,
    headers: headers as Record<string, string> | undefined,
    body,
    query: query as Record<string, string | readonly string[]> | undefined,
  };
}

async function fetchOnce(
  request: InterpolatedRequest,
  fetchImpl: typeof fetch,
  timeout: number,
  outerSignal: AbortSignal | undefined,
  setTimeoutImpl: typeof setTimeout = setTimeout,
  clearTimeoutImpl: typeof clearTimeout = clearTimeout,
): Promise<Response> {
  const controller = new AbortController();
  const onOuterAbort = (): void => {
    controller.abort(outerSignal?.reason);
  };
  if (outerSignal) {
    if (outerSignal.aborted) {
      controller.abort(outerSignal.reason);
    } else {
      outerSignal.addEventListener('abort', onOuterAbort, { once: true });
    }
  }
  const timer = setTimeoutImpl(() => {
    controller.abort(new Error('DataSource fetch timed out.'));
  }, timeout);

  try {
    const url = appendQuery(request.endpoint, request.query);
    const init: RequestInit = {
      method: request.method,
      headers: request.headers,
      signal: controller.signal,
    };
    if (request.method !== 'GET' && request.body !== undefined) {
      init.body = JSON.stringify(request.body);
      init.headers = {
        'content-type': 'application/json',
        ...(request.headers ?? {}),
      };
    }
    return await fetchImpl(url, init);
  } finally {
    clearTimeoutImpl(timer);
    if (outerSignal) {
      outerSignal.removeEventListener('abort', onOuterAbort);
    }
  }
}

function appendQuery(
  endpoint: string,
  query: Record<string, string | readonly string[]> | undefined,
): string {
  if (!query) {
    return endpoint;
  }
  const keys = Object.keys(query);
  if (keys.length === 0) {
    return endpoint;
  }
  const parts: string[] = [];
  for (const key of keys) {
    const value = query[key];
    const joined = Array.isArray(value) ? value.join(',') : String(value);
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(joined)}`);
  }
  const separator = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${separator}${parts.join('&')}`;
}

function backoffMs(attempt: number): number {
  return BASE_BACKOFF_MS * 2 ** attempt;
}

function defaultDelay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(
        toError(signal.reason, 'DataSource fetch aborted during backoff.'),
      );
      return;
    }
    const timer = setTimeout(() => {
      cleanup();
      resolve();
    }, ms);
    const onAbort = (): void => {
      clearTimeout(timer);
      cleanup();
      reject(
        toError(signal?.reason, 'DataSource fetch aborted during backoff.'),
      );
    };
    const cleanup = (): void => {
      signal?.removeEventListener('abort', onAbort);
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** True for the payloads a fetch reports as `empty`: `[]`, `{}`, `null`, `undefined`. */
export function isEmptyPayload(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).length === 0;
  }
  return false;
}

function isAbortError(error: unknown): boolean {
  if (error === null || typeof error !== 'object') {
    return false;
  }
  const name = (error as { name?: unknown }).name;
  return name === 'AbortError';
}

function toError(value: unknown, fallback: string): Error {
  if (value instanceof Error) {
    return value;
  }
  if (typeof value === 'string') {
    return new Error(value);
  }
  return new Error(fallback);
}
